/**
 * ADR-0166 — Six-section modal with the benchmarks methodology.
 *
 * Word-for-word port of `BenchmarkMethodologyModal` from
 * `docs/reference/Foyer_Dashboard_CURRENT.jsx` lines 7454–7523. The six
 * sections — DATA PIPELINE / K-ANONYMITY / WHAT IS NOT SHARED / WHAT IS
 * SHARED / OPT-OUT / NEVER FOR MARKETING — are the founder-approved copy
 * that anchors the benchmarks trust story. A positioning-pass rewording
 * may follow (marketing-agent task per CLAUDE.md §7), but is not blocking
 * for this port.
 */

import { Modal } from '../../components/atoms/index.js';
import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface BenchmarkMethodologyModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function BenchmarkMethodologyModal({
  open,
  onClose,
}: BenchmarkMethodologyModalProps): JSX.Element | null {
  const C = useC();
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title="Benchmarks methodology"
      subtitle="How aggregates are computed and protected."
    >
      <div
        className="p-5 space-y-5"
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.text,
          lineHeight: 1.6,
        }}
      >
        <section>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 2,
              marginBottom: 6,
            }}
          >
            DATA PIPELINE
          </div>
          <p style={{ color: C.muted }}>
            Aggregates are computed daily from an isolated pipeline that
            reads from each merchant's policy and outcome metadata. The
            pipeline writes a single aggregates table; the dashboard reads
            from that table. Individual merchant data is never accessible
            to the benchmarks read path.
          </p>
        </section>

        <section>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 2,
              marginBottom: 6,
            }}
          >
            K-ANONYMITY
          </div>
          <p style={{ color: C.muted }}>
            Any displayed statistic requires at least 10 merchants in the
            cohort. Category breakdowns require at least 30. Cohorts below
            those thresholds show "not enough data" — the underlying
            numbers exist but are not exposed.
          </p>
        </section>

        <section>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 2,
              marginBottom: 6,
            }}
          >
            WHAT IS NOT SHARED
          </div>
          <ul style={{ paddingLeft: 18, color: C.muted }}>
            <li>Dollar amounts, transaction values, GMV figures</li>
            <li>Customer information (emails, IDs, names)</li>
            <li>Merchant identifiers or domain names</li>
            <li>Specific mandate or audit details</li>
            <li>Free-text rule descriptions or custom DSL</li>
          </ul>
        </section>

        <section>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 2,
              marginBottom: 6,
            }}
          >
            WHAT IS SHARED
          </div>
          <ul style={{ paddingLeft: 18, color: C.muted }}>
            <li>Count of enabled rules per category</li>
            <li>Red-team coverage rates by category</li>
            <li>Block/escalate/allow outcome rates</li>
            <li>Mean decision time</li>
            <li>Industry and traffic volume band (you choose your own)</li>
          </ul>
        </section>

        <section>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 2,
              marginBottom: 6,
            }}
          >
            OPT-OUT
          </div>
          <p style={{ color: C.muted }}>
            Opting out stops new aggregation within 24 hours. Existing
            aggregates do not retroactively re-derive — but as new
            aggregates are computed, your data is no longer included. The
            opt-out is total: you also lose access to view benchmarks.
          </p>
        </section>

        <section>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 2,
              marginBottom: 6,
            }}
          >
            NEVER FOR MARKETING
          </div>
          <p style={{ color: C.muted }}>
            Benchmark data is scoped to opted-in merchant dashboards.
            Sill's sales and marketing teams have no access. The
            contractual representation is in the terms of service.
          </p>
        </section>

        <div className="flex justify-end pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2"
            style={{
              background: C.lime,
              color: C.ink,
              fontFamily: sansFont,
              fontSize: 13,
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            Got it
          </button>
        </div>
      </div>
    </Modal>
  );
}
